import { useReducer } from 'react';
import { useMediaQuery } from 'react-responsive';
import qs from 'qs';
import ProjectOnboardingContext from './Context';
import Logger from 'utilities/logger';
import Valid from 'utilities/valid';
import useFeatureFlags from 'hooks/useFeatureFlags';
import useSnackbar from 'hooks/useSnackbar';

const FLAG_PROJECT_ONBOARDING_VIACOM_PA = 'MyCnC-Project-Onboarding-Viacom-PA';
const FLAG_PROJECT_ONBOARDING_VIACOM_EOR =
  'MyCnC-Project-Onboarding-Viacom-EOR';
const FLAG_PROJECT_ONBOARDING_VIACOM_CRANETOWN =
  'MyCnC-Project-Onboarding-Viacom-Cranetown';
const FLAG_PROJECT_ONBOARDING_NONUNION_UNION_REDESIGN =
  'MyCnC-Project-Onboarding-Nonunion-Union-Redesign';
const FLAG_PROJECT_ONBOARDING_RESIDUALS = 'MyCnC-Projects-Onboarding-Residuals';
const FLAG_CANADA_REGION = 'MyCnC-Project-Onboarding-Canada-Region';
const FLAG_PROJECT_ONBOARDING_REPORTING_PLUS =
  'MyCnC-Project-Onboarding-ReportingPlus';
const FLAG_PROJECT_ONBOARDING_STUDIO_INVOICE_APPROVER =
  'MyCnC-Project-Onboarding-Studio-Invoice-Approver';
const FLAG_PROJECT_ONBOARDING_REQUIRED_FIELDS_ENABLED_BUTTON =
  'MyCnC-Project-Onboarding-Required-Fields-Enabled-Button';
const FLAG_AMAZON_CONTROLLED =
  'MyCnC-Project-Onboarding-AmazonProjectControlled';
const FLAG_PROJECT_ONBOARDING_TEMPLATE_ALL_OPTIONAL =
  'MyCnC-Project-Onboarding-Template-All-Optional-Fields';
const FLAG_PROJECT_ONBOARDING_START_DEPARTMENTS =
  'MyCnC-Project-Onboarding-StartDepartments';
const FLAG_PROJECT_ONBOARDING_START_PERMISSIONS =
  'MyCnC-Project-Onboarding-Required-StartPlus-Permissions';
const FLAG_PROJECT_ONBOARDING_UNION_SIGNATORIES =
  'MyCnC-Project-Onboarding-Union-Signatories';
const FLAG_PROJECT_ONBOARDING_MANAGE_COLLABORATORS =
  'MyCnC-Project-Onboarding-ManageCollaborators';
const FLAG_PROJECT_ONBOARDING_ACCOUNTING_FIRM =
  'MyCnC-Project-Onboarding-Accounting-Firm';
const FLAG_PROJECT_ONBOARDING_USER_JOB_TITLE =
  'MyCnC-Project-Onboarding-Required-User-Job-Title';

const ProjectOnboardingProvider = ({ children }) => {
  const { fromURI } = qs.parse((location.search || '?').slice(1));
  const { isEnabled } = useFeatureFlags();
  const notify = useSnackbar();
  const featureFlags = {
    viacomPaEnabled: isEnabled(FLAG_PROJECT_ONBOARDING_VIACOM_PA) || false,
    viacomEorEnabled: isEnabled(FLAG_PROJECT_ONBOARDING_VIACOM_EOR) || false,
    viacomCtEnabled:
      isEnabled(FLAG_PROJECT_ONBOARDING_VIACOM_CRANETOWN) || false,
    residualsEnabled: isEnabled(FLAG_PROJECT_ONBOARDING_RESIDUALS) || false,
    nonUnionUnionRedesignEnabled:
      isEnabled(FLAG_PROJECT_ONBOARDING_NONUNION_UNION_REDESIGN) || false,
    stub: false,
    canadaEnabled: isEnabled(FLAG_CANADA_REGION) || false,
    reportingPlusEnabled:
      isEnabled(FLAG_PROJECT_ONBOARDING_REPORTING_PLUS) || false,
    studioInvoiceApproverEnabled:
      isEnabled(FLAG_PROJECT_ONBOARDING_STUDIO_INVOICE_APPROVER) || false,
    requiredFieldsNextEnabled:
      isEnabled(FLAG_PROJECT_ONBOARDING_REQUIRED_FIELDS_ENABLED_BUTTON) ||
      false,
    enableAmazonControlled: isEnabled(FLAG_AMAZON_CONTROLLED) || false,
    templateAllOptionalEnabled:
      isEnabled(FLAG_PROJECT_ONBOARDING_TEMPLATE_ALL_OPTIONAL) || false,
    startPlusDepartmentsEnabled:
      isEnabled(FLAG_PROJECT_ONBOARDING_START_DEPARTMENTS) || false,
    startPlusPermissionsEnabled:
      isEnabled(FLAG_PROJECT_ONBOARDING_START_PERMISSIONS) || false,
    unionSignatoriesEnabled:
      isEnabled(FLAG_PROJECT_ONBOARDING_UNION_SIGNATORIES) || false,
    manageCollaboratorsEnabled:
      isEnabled(FLAG_PROJECT_ONBOARDING_MANAGE_COLLABORATORS) || false,
    accountingFirmEnabled:
      isEnabled(FLAG_PROJECT_ONBOARDING_ACCOUNTING_FIRM) || false,
    requiredUserJobTitleEnabled:
      isEnabled(FLAG_PROJECT_ONBOARDING_USER_JOB_TITLE) || false,
  };

  const isMobile = useMediaQuery({
    query: '(max-width: 960px)',
  });

  const baseProjectState = {
    // Variables that affect project view
    project: null,
    originalProject: null,
    step: 'ProductsServices',
    // [clientKnowledgeBase | history | contactUs | manageCollaborators]
    sidePanel: 'clientKnowledgeBase',
    willDuplicate: false,
    fromOptionsMenu: false,
    processingCollaborators: false /* Need to put this in upper state to survive re-render of the panel */,
    permission:
      '' /* 'Submitter' | 'View'  -> A Creator also gets 'Submitter' */,
    submitProjectStep: 'StudioPlus',
    sectionTitle: null,
    autoPopulateProducerDetails: false,
    autoPopulateProdCoDetails: false,
    saveChangesDisabled: true,
    showChangesPrompt: false,
    isViacom: false,
    isPayingAgent: false,
    templateBanner: false,
    viewOnlyBanner: false,
    isDirty: false,
    invitedBy: {},
    isMobile: isMobile,
    compactViewMode: false,
  };

  const [state, changeState] = useReducer(
    (state, data) => ({ ...state, ...data }),
    {
      fromURI: Valid.fromURI(fromURI),
      projects: [],
      loading: false, // General operation, like updating or creating a project
      listLoading: false, // first load of the list
      enableNewProject: false,
      ...baseProjectState,
      ...featureFlags,
    },
  );

  const resetContext = () => {
    changeState({
      project: null,
      ...baseProjectState,
    });
  };

  const logger = Logger('ProjectsOnboarding');
  const value = {
    featureFlags,
    state,
    changeState,
    resetContext,
    logger,
    notify,
  };
  return (
    <ProjectOnboardingContext.Provider value={value}>
      {children}
    </ProjectOnboardingContext.Provider>
  );
};

export default ProjectOnboardingProvider;
